import browserStore from '@anm/helpers/browserStore';
import parseDestinationExtra from '@anm/helpers/stream/parseDestinationExtra';
import useSwitchState from '@anm/hooks/useSwitchState';
import { userDestinationsSelectors } from '@anm/store/modules/userDestinations';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { SocialDestination, StreamingNetwork } from 'types/stream';

import getDestinationStorageKey from '../helpers/getDestinationStorageKey';
import { DestinationTypes } from '../types';

import { DestinationParams } from './../types';
import { FACEBOOK_PARAM, LINKEDIN_PARAM } from './useAddedDestination';

const destinationTypes: DestinationTypes[] = ['page', 'group', 'profile'];

const isGroupAdmin = (d: SocialDestination) => {
  const destinationParams = parseDestinationExtra(d);

  return !!destinationParams['admin'];
};

const useChooseAccountModal = () => {
  const storageKey = getDestinationStorageKey();
  const [destinationType, setDestinationType] = useState<DestinationTypes>();
  const [filteredDestinations, setFilteredDestinations] = useState<SocialDestination[] | null>(null);
  const [isAdminOnlyGroups, setIsAdminOnlyGroups] = useState(false);
  const [network, setNetwork] = useState<StreamingNetwork>();

  const allDestinations = useSelector(userDestinationsSelectors.selectDestinationsList);

  const isFetched = useSelector(userDestinationsSelectors.selectDestinationsListIsFetched);

  const [chooseAccountModalOpened, openChooseAccountModal, closeChooseAccountModal] = useSwitchState();

  useEffect(() => {
    const params = browserStore.get(storageKey) as DestinationParams;

    if (!allDestinations?.length || !params || !isFetched) {
      return;
    }

    const isFacebookAdded = params.network === FACEBOOK_PARAM;
    const isLinkedinAdded = params.network === LINKEDIN_PARAM;
    const destinationtype = params.type;
    const isReconnect = params.isReconnect;

    if (isReconnect) {
      browserStore.remove(storageKey);
      return;
    }

    if (!isFacebookAdded && !isLinkedinAdded) {
      return;
    }

    const network = isFacebookAdded ? 'FACEBOOK' : 'LINKEDIN';
    const isAdminOnlyGroups = destinationtype === 'group_admin';
    const destinationType = isAdminOnlyGroups ? 'group' : (destinationtype as DestinationTypes);

    if (!destinationTypes.includes(destinationType) || !destinationType) {
      return;
    }

    const destinations = allDestinations
      ?.filter(d => d.kind === destinationType && d.network === network && (isAdminOnlyGroups ? isGroupAdmin(d) : true))
      .map(d => ({ ...d, isConnected: d.enabled }));

    setNetwork(network);
    setIsAdminOnlyGroups(isAdminOnlyGroups);
    setDestinationType(destinationType);
    setFilteredDestinations(destinations);

    destinations?.length && openChooseAccountModal();
  }, [allDestinations, isFetched]);

  const handleCloseChooseAccountModal = () => {
    closeChooseAccountModal();
    browserStore.remove(storageKey);
  };

  return [
    network,
    isAdminOnlyGroups,
    chooseAccountModalOpened,
    destinationType,
    filteredDestinations,
    handleCloseChooseAccountModal
  ] as const;
};

export default useChooseAccountModal;
